/**
 * FSE SegmentedButton — MD3 connected single/multi-select button group.
 *
 * Design source: `Documentation/Claude design files/preview/segmented-button.html`.
 * Spec: connected group with a 2dp gap, 8dp inner corners and a full (pill)
 * outer shape — the first/last segments round their outer edges to `radius.full`
 * while inner edges stay at `radius.cornerSmall`. The selected segment uses the
 * leaf secondary-container colour with bolder weight. Optional leading icon per
 * option is rendered as an inline Material Symbol.
 *
 * Built on MUI `ToggleButtonGroup`/`ToggleButton`. Single-select returns a
 * string; multi-select returns a string[]. Public prop surface is FSE-owned —
 * MUI's `sx` is never exposed.
 */
import {
  ToggleButton,
  ToggleButtonGroup,
  type ToggleButtonGroupProps,
} from '@mui/material';
import { forwardRef } from 'react';
import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor, radius, tap, typography } from '../theme/tokens.js';
import { Icon } from './Icon.js';

export interface SegmentedButtonOption {
  value: string;
  label: string;
  /** Optional leading Material Symbols icon name (e.g. "check"). */
  icon?: string;
}

export interface SegmentedButtonProps {
  /** Segments, left to right. */
  options: SegmentedButtonOption[];
  /** Selected value — `string` for single-select, `string[]` when `multiple`. */
  value: string | string[];
  /** Fires with the next selection (`string | null` single, `string[]` multi). */
  onChange: (value: string | string[] | null) => void;
  /** Allow multiple selections. Default false (single-select, mandatory). */
  multiple?: boolean;
  /** Disable the whole group. */
  disabled?: boolean;
  /** Accessible label for the group. */
  'aria-label'?: string;
}

export const SegmentedButton = forwardRef<HTMLDivElement, SegmentedButtonProps>(
  function SegmentedButton(
    { options, value, onChange, multiple = false, disabled = false, 'aria-label': ariaLabel },
    ref,
  ) {
    // Mode-aware: in light these roles resolve to the same brand values the
    // static styles used (outline=earth[500], onSurface=earth[900],
    // secondaryContainer=leaf[100], onSecondaryContainer=leaf[900]).
    const { mode } = useColorMode();
    const sys = mdSysColor[mode];
    const handleChange: NonNullable<ToggleButtonGroupProps['onChange']> = (_e, next) => {
      // MUI passes `null` (single, deselected) or `string[]` (multi); forward as-is.
      onChange(next as string | string[] | null);
    };

    return (
      <ToggleButtonGroup
        ref={ref}
        value={value}
        exclusive={!multiple}
        onChange={handleChange}
        disabled={disabled}
        aria-label={ariaLabel}
        sx={{
          display: 'flex',
          gap: '2px',
          '& .MuiToggleButtonGroup-grouped': {
            flex: 1,
            minHeight: tap.sm, // ≥48dp, rural-bumped
            textTransform: 'none',
            border: `1px solid ${sys.outline}`,
            color: sys.onSurface,
            fontFamily: typography.fontFamily,
            fontWeight: 600,
            fontSize: typography.labelLarge.fontSize,
            gap: '6px',
            // Reset MUI's connected-group margin/border collapsing — we use a real gap.
            margin: 0,
            borderRadius: `${radius.cornerSmall}px`,
            '&:not(:first-of-type)': {
              borderLeft: `1px solid ${sys.outline}`,
              marginLeft: 0,
            },
            '&:first-of-type': {
              borderTopLeftRadius: `${radius.full}px`,
              borderBottomLeftRadius: `${radius.full}px`,
            },
            '&:last-of-type': {
              borderTopRightRadius: `${radius.full}px`,
              borderBottomRightRadius: `${radius.full}px`,
            },
            '&.Mui-selected': {
              backgroundColor: sys.secondaryContainer,
              color: sys.onSecondaryContainer,
              fontWeight: 700,
              '&:hover': {
                backgroundColor: sys.secondaryContainer,
              },
            },
          },
        }}
      >
        {options.map((opt) => (
          <ToggleButton key={opt.value} value={opt.value} aria-label={opt.label}>
            {opt.icon && (
              <Icon name={opt.icon} size={18} />
            )}
            {opt.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  },
);
